import { createSlice, PayloadAction, Dispatch } from '@reduxjs/toolkit';
import { OptionType } from 'components/UI/SelectField';
import { APIError } from 'models';
import {
  SoftwareFileUpload,
  Software,
  SoftwareFilter,
  SoftwareState,
  SoftwareSuccess,
  SoftwareUploadAttributes,
  ReleaseSoftwareRequest,
  UpdateSoftwareRequest,
} from 'models/software';
import { FieldValues } from 'react-hook-form';
import {
  findSoftwares,
  getFileUploadUrl,
  getSoftwareById,
  releaseSoftware,
  updateSoftware,
  uploadFileToS3Bucket,
  uploadSoftware,
} from 'services/softwareService/softwareService';

export const defaultState: SoftwareState = {
  list: {
    data: null,
    totalCount: 0,
    isLoading: false,
    error: null,
    filters: {
      active: false,
      data: null,
    },
  },
  uploadSoftware: {
    data: null,
    isLoading: false,
    error: null,
  },
  softwareDetail: {
    data: null,
    isLoading: false,
    error: null,
    isUpdatedSuccessfully: false,
  },
};

// Export slice
export const softwareSlice = createSlice({
  name: 'software',
  initialState: defaultState,
  reducers: {
    getSoftwares: (state) => {
      state.list.isLoading = true;
      state.list.error = null;
    },
    getSoftwaresSuccess: (state, action: PayloadAction<SoftwareSuccess>) => {
      state.list.isLoading = false;
      state.list.error = null;
      state.list.data = action.payload.response;
      state.list.totalCount = action.payload.totalCount;
      state.list.filters.data = action.payload.filters;
    },
    getSoftwaresFailure: (state, action: PayloadAction<APIError>) => {
      state.list.data = [];
      state.list.totalCount = 0;
      state.list.isLoading = false;
      state.list.error = action.payload;
    },
    getSoftwareDetails: (state) => {
      state.softwareDetail.isLoading = true;
      state.softwareDetail.error = null;
      state.softwareDetail.isUpdatedSuccessfully = false;
    },
    getSoftwareDetailsSuccess: (state, action: PayloadAction<Software>) => {
      state.softwareDetail.isLoading = false;
      state.softwareDetail.error = null;
      state.softwareDetail.data = action.payload;
    },
    updateSoftwareDetailsSuccess: (state, action: PayloadAction<Software>) => {
      state.softwareDetail.isLoading = false;
      state.softwareDetail.error = null;
      state.softwareDetail.data = action.payload;
      state.softwareDetail.isUpdatedSuccessfully = true;
    },
    getSoftwareDetailsFailure: (state, action: PayloadAction<APIError>) => {
      state.softwareDetail.data = null;
      state.softwareDetail.isLoading = false;
      state.softwareDetail.error = action.payload;
      state.softwareDetail.isUpdatedSuccessfully = false;
    },
    setSoftwareFilters: (state, action: PayloadAction<boolean>) => {
      state.list.filters.active = action.payload;
    },

    saveSoftware: (state) => {
      state.uploadSoftware.isLoading = true;
      state.uploadSoftware.error = null;
    },
    saveSoftwareSuccess: (state, action: PayloadAction<Software>) => {
      state.uploadSoftware.isLoading = false;
      state.uploadSoftware.error = null;
      state.uploadSoftware.data = action.payload;
    },
    saveSoftwareFailure: (state, action: PayloadAction<APIError>) => {
      state.uploadSoftware.data = null;
      state.uploadSoftware.isLoading = false;
      state.uploadSoftware.error = action.payload;
    },
  },
});

// Export actions
export const {
  getSoftwares,
  getSoftwaresSuccess,
  getSoftwaresFailure,
  setSoftwareFilters,
  saveSoftware,
  saveSoftwareSuccess,
  saveSoftwareFailure,
  getSoftwareDetails,
  getSoftwareDetailsSuccess,
  updateSoftwareDetailsSuccess,
  getSoftwareDetailsFailure,
} = softwareSlice.actions;

// Export reducer
export const softwareReducer = softwareSlice.reducer;

export const softwareListSelector = (state: any) => state.software.list;
export const softwareUploadSelector = (state: any) =>
  state.software.uploadSoftware;
export const softwareDetailsSelector = (state: any) =>
  state.software.softwareDetail;

const createSoftwareRequest = (
  fileUploadResponse: SoftwareFileUpload,
  software: FieldValues,
  purposes: string[]
): SoftwareUploadAttributes => {
  return {
    name: software.name,
    pnc: software.pnc,
    revision: software.revision,
    version: software.version,
    firmwareVersion: software.firmwareVersion,
    mimetype: software.mimeType,
    purposes: purposes,
    category: software.category,
    bundleId: software.bundelId,
    releaseStage: software.releaseStage,
    releasedFor: software.releasedFor,
    file: {
      id: fileUploadResponse.id,
      fileName: software.files[0].name,
    },
  };
};
// Export thunk
export const fetchSoftwareList =
  /* prettier-ignore */
  (filter: SoftwareFilter[], offset: number) => async (dispatch: Dispatch) => {
    dispatch(getSoftwares());
    try {
      const limit = 10;
      const response = await findSoftwares(offset, limit, filter);
      dispatch(
        getSoftwaresSuccess({
          response: response.data,
          totalCount: response.meta.totalCount,
          filters: response.data.length ? filter : null,
        })
      );
    } catch (e: any) {
      dispatch(getSoftwaresFailure(e.errors[0]));
    }
  };
export const storeSoftware =
  /* prettier-ignore */
  (software: FieldValues, purposes: OptionType[]) => async (dispatch: Dispatch) => {
    dispatch(saveSoftware());
    try {
      if (software.files.length !== 1) {
        const apiError: APIError = {
          status: 500,
          code: '500',
          detail: `Try upload ${software.files.length} files`,
          title: 'One and only one file required',
        };
        throw apiError;
      }
      // request presigned url
      const fileUploadResponse = await getFileUploadUrl();
      // upload file to s3 with presigned url
      await uploadFileToS3Bucket(
        fileUploadResponse.attributes.url,
        software.files[0]
      );
      const purposesToSave: string[] = [];
      purposes.forEach((a) => purposesToSave.push(a.value));

      const softwareToSave: SoftwareUploadAttributes = createSoftwareRequest(
        fileUploadResponse,
        software,
        purposesToSave
      );
      const response = await uploadSoftware(softwareToSave);
      dispatch(saveSoftwareSuccess(response));
      // Save software
    } catch (e: any) {
      dispatch(saveSoftwareFailure(e?.errors?.[0] || e));
    }
  };

export const fetchSoftwareById = (id: string) => async (dispatch: Dispatch) => {
  dispatch(getSoftwareDetails());
  try {
    const response = await getSoftwareById(id);
    dispatch(getSoftwareDetailsSuccess(response));
  } catch (e: any) {
    dispatch(getSoftwareDetailsFailure(e.errors[0]));
  }
};

export const releaseSoftwareToProduct =
  /* prettier-ignore */
  (softwareId: string, pnc: string, releaseDate: Date) => async (dispatch: Dispatch) => {
    dispatch(getSoftwareDetails());
    try {
      const releaseSoftwareRequest: ReleaseSoftwareRequest = {
        data: {
          id: softwareId,
          type: 'releasesoftware',
          attributes: {
            pnc,
            releaseDate: releaseDate.getTime(),
          },
        }
      };
      await releaseSoftware(releaseSoftwareRequest);
      const response = await getSoftwareById(softwareId);
      dispatch(getSoftwareDetailsSuccess(response));
    } catch (e: any) {
      dispatch(getSoftwareDetailsFailure(e.errors[0]));
    }
  };

export const updateReleaseStageForSoftware =
  (software: FieldValues, softwareId: string) => async (dispatch: Dispatch) => {
    dispatch(getSoftwareDetails());
    try {
      const request: UpdateSoftwareRequest = {
        data: {
          id: softwareId,
          type: 'software',
          attributes: {
            releaseStage: software.releaseStage,
          },
        },
      };
      await updateSoftware(request);
      const response = await getSoftwareById(softwareId);
      dispatch(updateSoftwareDetailsSuccess(response));
    } catch (e: any) {
      dispatch(getSoftwareDetailsFailure(e.errors[0]));
    }
  };
